'use client'

import { AddIcon, RemoveIcon } from '../../icons'
import React, { ChangeEvent, HTMLAttributes, ReactNode, Ref } from 'react'
import { ChangeHandler } from 'react-hook-form'
import useInputWrapper from '../../../hooks/useInputWrapper'
import { Button, ButtonVariants } from '../../atoms'

export enum ControlsPosition {
    LEFT = 'left',
    SPLIT = 'split',
    RIGHT = 'right',
}

export interface FormNumberInputProps extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
    disabled?: boolean
    form?: string
    readOnly?: boolean
    error?: boolean
    id?: string
    name?: string
    prefix?: string
    suffix?: string | ReactNode
    showControls?: boolean
    controlsPosition?: ControlsPosition
    min?: number
    max?: number
    step?: number
    onChange?: (value: number | string) => void
    onBlur?: ChangeHandler
    onClickSuffix?: () => void
    value?: number | string
    autoFocus?: boolean
    required?: boolean
    inputRef?: Ref<HTMLInputElement>
}

export const FormNumberInput = ({
    disabled,
    error,
    prefix,
    form,
    suffix,
    showControls,
    controlsPosition = ControlsPosition.RIGHT,
    min = 0,
    max,
    step = 1,
    onChange,
    onBlur,
    onClickSuffix,
    readOnly = false,
    value,
    autoFocus = false,
    inputRef,
    ...rest
}: FormNumberInputProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled, error)

    const controlsAreOnLeft: boolean = controlsPosition === ControlsPosition.LEFT
    const controlsAreSplit: boolean = controlsPosition === ControlsPosition.SPLIT

    const increment = () => {
        if (typeof value !== 'number') {
            onChange?.(step)
            return
        }

        const newValue: number = (value ?? min) + step
        onChange?.(typeof max === 'number' ? Math.min(newValue, max) : newValue)
    }

    const decrement = () => {
        if (typeof value !== 'number') {
            onChange?.(min)
            return
        }

        const newValue: number = (value ?? min) - step
        onChange?.(Math.max(newValue, min))
    }

    const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        const inputValue: string = event.target.value
        const hasEmptyInput: boolean = inputValue === ''
        const value: string | number = hasEmptyInput ? inputValue : parseFloat(inputValue)
        onChange?.(value)
    }

    const getInputClasses = (): string => {
        let inputClasses = 'w-full px-3 py-2 density-lg:py-3 flex-1 outline-0 bg-transparent placeholder:italic'
        if (controlsAreOnLeft) inputClasses += 'text-right'
        if (controlsAreSplit) inputClasses += ' text-center'
        return inputClasses
    }

    return (
        <div className={`flex items-center border w-full ${classes} ${rest.className || ''}`} data-cy="input-number">
            {prefix && <span className="pl-3 text-shade-600">{prefix}</span>}

            <input
                {...rest}
                disabled={disabled}
                readOnly={readOnly}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                form={form}
                className={getInputClasses()}
                onFocus={onFocusHandler}
                onBlur={(event) => {
                    onBlur && onBlur(event)
                    onBlurHandler()
                }}
                onChange={changeHandler}
                value={value}
                min={min}
                max={max}
                step={step}
                type="number"
                autoFocus={autoFocus}
                formNoValidate
                aria-invalid={error}
                ref={inputRef}
            />

            {suffix && !onClickSuffix && <span className="pr-3 text-shade-600">{suffix}</span>}

            {suffix && onClickSuffix && (
                <Button variant={ButtonVariants.QUIET} onClick={onClickSuffix}>
                    {suffix}
                </Button>
            )}

            {showControls && (
                <>
                    <Button
                        data-testid="decrement-button"
                        variant={ButtonVariants.QUIET}
                        className={`select-none -my-1px ${controlsAreOnLeft || controlsAreSplit ? '-order-2' : ''}`}
                        onClick={() => decrement()}
                        icon={<RemoveIcon className="text-xxl " />}
                    />
                    <Button
                        data-testid="increment-button"
                        variant={ButtonVariants.QUIET}
                        className={`select-none -my-1px ${controlsAreOnLeft ? '-order-1' : ''}`}
                        onClick={() => increment()}
                        icon={<AddIcon className="text-xxl " />}
                    />
                </>
            )}
        </div>
    )
}
